import { Clinic, ClinicResponse } from "services/types/clinics.type";

export const getClinicDate = (params: {
  clinic: Clinic | undefined | null;
  date?: Date | null;
  clearTime?: boolean;
}) => {
  const { clinic, date, clearTime } = params;
  if (!clinic?.timezone) return null;

  const result = new Date(
    (date ?? new Date()).toLocaleString("en-US", { timeZone: clinic?.timezone })
  );

  if (clearTime) {
    result.setHours(0);
    result.setMinutes(0);
    result.setMilliseconds(0);
  }

  return result;
};

export const formatClinic = (clinic: ClinicResponse) => {
  const { tilled_account_id, justifi_account_id, practice, ...clinicAttrs } =
    clinic || {};
  return {
    tilledAccountId: tilled_account_id,
    justifiAccountId: justifi_account_id || practice?.justifi_account_id,
    practice,
    ...clinicAttrs,
    fullName: `${clinic?.practice?.name ?? ""} ${clinic?.name ?? ""}`.trim()
  };
};

export const getSelectedClinicIds = (
  selectedClinicIds?: string[] | number[] | null
) => {
  if (Array.isArray(selectedClinicIds) && selectedClinicIds.length) {
    return selectedClinicIds.map((clinicId) => Number(clinicId));
  }
  return undefined;
};

export const determineClinicFiltering = ({
  currentRegionClinicsIds,
  currentClinic,
  includeOtherClinics,
  isShowOwnClinicsOnlyEnabled,
  userClinicsIds
}: {
  currentClinic: Clinic | undefined;
  includeOtherClinics: boolean;
  currentRegionClinicsIds?: number[];
  isShowOwnClinicsOnlyEnabled?: boolean;
  userClinicsIds?: number[];
}) => {
  if (includeOtherClinics) {
    return isShowOwnClinicsOnlyEnabled
      ? userClinicsIds?.length
        ? userClinicsIds
        : currentClinic?.id
      : currentRegionClinicsIds;
  }

  return currentClinic?.id ?? undefined;
};

/**
 * How staff-facing screens name a clinic: by its own name ("Chevy Chase"), not its display name.
 * `display_name` is what PATIENTS see on invoices, statements and messages, and multi-location
 * practices routinely give every clinic the practice brand there, so labelling a staff picker
 * with it renders every clinic identically (Intercom 215476285526105). `patientFacingName` is
 * returned only when it differs from `name`, for a secondary "Patients see: …" line.
 */
export const getClinicStaffLabel = (
  clinic:
    | { name?: string | null; display_name?: string | null }
    | null
    | undefined
) => {
  const ownName = clinic?.name?.trim() ?? "";
  const displayName = clinic?.display_name?.trim() ?? "";
  const name = ownName || displayName;
  // Clinic names use a case-insensitive collation, so "MAIN" and "Main" are the same name.
  const patientFacingName =
    displayName && displayName.toLowerCase() !== name.toLowerCase()
      ? displayName
      : null;
  return { name, patientFacingName };
};

/**
 * Labels for the top-bar clinic dropdown (CLINIC_SELECTOR_DISPLAY_NAME_FEATURE): a clinic is listed
 * by its own display name when one is set on the clinic (`own_display_name`, never the practice
 * fallback that `display_name` resolves to), else by `fullName` ("<practice> <clinic>").
 * Multi-location practices often give clinics their patient-facing brand, so a display name is NOT
 * used (case-insensitive) when it:
 * - is just the practice name: the row would no longer say which location it is;
 * - is shared with another listed clinic's display name, or equals another clinic's `fullName`:
 *   two rows would read the same.
 * Every label this returns is therefore distinct, as long as the `fullName`s are.
 */
export const getClinicSelectorLabels = (
  clinics: Array<
    Pick<Clinic, "id" | "fullName" | "own_display_name"> & {
      practice?: { name?: string | null } | null;
    }
  >
): Map<number, string> => {
  const normalize = (value?: string | null) =>
    value?.trim().toLowerCase() ?? "";
  const displayNameCounts = new Map<string, number>();
  clinics.forEach((clinic) => {
    const key = normalize(clinic.own_display_name);
    if (key) displayNameCounts.set(key, (displayNameCounts.get(key) ?? 0) + 1);
  });
  const fullNameOwners = new Map(
    clinics.map((clinic) => [normalize(clinic.fullName), clinic.id])
  );
  return new Map(
    clinics.map((clinic) => {
      const displayName = clinic.own_display_name?.trim() ?? "";
      const key = normalize(displayName);
      const fullNameOwner = fullNameOwners.get(key);
      const usable =
        Boolean(key) &&
        key !== normalize(clinic.practice?.name) &&
        displayNameCounts.get(key) === 1 &&
        (fullNameOwner === undefined || fullNameOwner === clinic.id);
      return [clinic.id, usable ? displayName : clinic.fullName];
    })
  );
};
